/**
 * Lafortune Premium Jigsaw Puzzle
 * Minimal, mysterious, premium experience
 */

(function() {
  'use strict';

  class LafortunePuzzle {
    constructor(options) {
      this.sectionId = options.sectionId;
      this.puzzleImageUrl = options.puzzleImageUrl || '';
      this.videoUrl = options.videoUrl || '';
      this.hintImageUrl = options.hintImageUrl || '';
      this.codePool = options.codePool || [];
      this.piecesCount = window.innerWidth < 750 ? 16 : (options.piecesCount || 16);
      
      this.gridSize = Math.sqrt(this.piecesCount);
      this.pieces = [];
      this.slots = [];
      this.board = [];
      this.pieceSize = 0;
      this.completed = false;
      this.image = null;
      
      this.activePiece = null;
      this.offsetX = 0;
      this.offsetY = 0;
      
      this.init();
    }

    async init() {
      const section = document.querySelector(`[data-section-id="${this.sectionId}"]`);
      if (!section) return;
      
      this.section = section;
      this.container = section.querySelector('.puzzle-container');
      this.piecesArea = section.querySelector('.puzzle-pieces');
      this.boardArea = section.querySelector('.puzzle-board');
      this.successOverlay = section.querySelector('.puzzle-success-overlay');
      this.videoContainer = section.querySelector('.puzzle-video-container');
      
      if (!this.container || !this.piecesArea || !this.boardArea) return;
      if (!this.puzzleImageUrl) return;

      try {
        await this.loadImage();
        this.calculateSizes();
        this.createBoard();
        this.createPieces();
        this.setupButtons();
      } catch (e) {
        console.error('Failed to initialize puzzle:', e);
      }
    }

    loadImage() {
      return new Promise((resolve, reject) => {
        this.image = new Image();
        this.image.crossOrigin = 'anonymous';
        this.image.onload = resolve;
        this.image.onerror = reject;
        this.image.src = this.puzzleImageUrl;
      });
    }

    calculateSizes() {
      const rect = this.container.getBoundingClientRect();
      const maxSize = Math.min(rect.width - 24, rect.height - 16);
      const boardSize = Math.max(140, maxSize * 0.95);
      this.pieceSize = Math.floor(boardSize / this.gridSize);
      this.boardSize = this.pieceSize * this.gridSize;
    }

    createBoard() {
      this.boardArea.innerHTML = '';
      const totalSize = this.boardSize + (this.gridSize - 1) + 6;
      
      this.boardArea.style.cssText = `
        display: grid;
        grid-template-columns: repeat(${this.gridSize}, ${this.pieceSize}px);
        grid-template-rows: repeat(${this.gridSize}, ${this.pieceSize}px);
        gap: 1px;
        width: ${totalSize}px;
        background: rgba(255,255,255,0.2);
        border: 1px solid rgba(255,255,255,0.3);
        border-radius: 4px;
        padding: 2px;
        position: relative;
        z-index: 1;
        transition: all 0.4s ease;
      `;

      for (let row = 0; row < this.gridSize; row++) {
        this.board[row] = [];
        for (let col = 0; col < this.gridSize; col++) {
          const slot = document.createElement('div');
          slot.dataset.row = row;
          slot.dataset.col = col;
          slot.style.cssText = `
            width: ${this.pieceSize}px;
            height: ${this.pieceSize}px;
            background: rgba(0,0,0,0.25);
            border: 1px solid rgba(255,255,255,0.15);
            box-sizing: border-box;
          `;
          this.boardArea.appendChild(slot);
          this.slots.push({ element: slot, row, col });
          this.board[row][col] = null;
        }
      }
    }

    createPieces() {
      this.piecesArea.innerHTML = '';
      this.piecesArea.style.cssText = `
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        z-index: 10;
        overflow: visible;
        pointer-events: none;
      `;

      const indices = Array.from({ length: this.piecesCount }, (_, i) => i);
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }

      const areaRect = this.piecesArea.getBoundingClientRect();
      const areaW = areaRect.width || 280;
      const areaH = areaRect.height || 450;

      indices.forEach((idx) => {
        const row = Math.floor(idx / this.gridSize);
        const col = idx % this.gridSize;

        const piece = document.createElement('div');
        piece.dataset.row = row;
        piece.dataset.col = col;
        piece.dataset.idx = idx;

        const canvas = document.createElement('canvas');
        canvas.width = this.pieceSize * 2;
        canvas.height = this.pieceSize * 2;
        
        const ctx = canvas.getContext('2d');
        const srcX = (col / this.gridSize) * this.image.width;
        const srcY = (row / this.gridSize) * this.image.height;
        const srcW = this.image.width / this.gridSize;
        const srcH = this.image.height / this.gridSize;
        ctx.drawImage(this.image, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
        
        piece.appendChild(canvas);

        const x = 8 + Math.random() * (areaW - this.pieceSize - 16);
        const y = 8 + Math.random() * (areaH - this.pieceSize - 16);

        piece.style.cssText = `
          position: absolute;
          left: ${x}px;
          top: ${y}px;
          width: ${this.pieceSize}px;
          height: ${this.pieceSize}px;
          cursor: grab;
          touch-action: none;
          user-select: none;
          -webkit-user-select: none;
          pointer-events: auto;
          border-radius: 2px;
          box-shadow: 0 3px 10px rgba(0,0,0,0.5);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          z-index: 100;
        `;

        canvas.style.cssText = `
          width: 100%;
          height: 100%;
          display: block;
          pointer-events: none;
          border-radius: 2px;
        `;

        piece.addEventListener('mousedown', (e) => this.handleDragStart(e, piece));
        piece.addEventListener('touchstart', (e) => this.handleDragStart(e, piece), { passive: false });

        this.piecesArea.appendChild(piece);
        this.pieces.push({ element: piece, row, col, placed: false });
      });

      document.addEventListener('mousemove', (e) => this.handleDragMove(e));
      document.addEventListener('mouseup', () => this.handleDragEnd());
      document.addEventListener('touchmove', (e) => this.handleDragMove(e), { passive: false });
      document.addEventListener('touchend', () => this.handleDragEnd());
      document.addEventListener('touchcancel', () => this.handleDragEnd());
    }

    handleDragStart(e, pieceEl) {
      if (this.completed || pieceEl.dataset.placed === 'true') return;

      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches ? e.touches[0] : e;
      const rect = pieceEl.getBoundingClientRect();

      this.activePiece = pieceEl;
      this.offsetX = touch.clientX - rect.left;
      this.offsetY = touch.clientY - rect.top;

      pieceEl.style.cursor = 'grabbing';
      pieceEl.style.zIndex = '1000';
      pieceEl.style.transform = 'scale(1.08)';
      pieceEl.style.boxShadow = '0 8px 25px rgba(0,0,0,0.6)';

      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    }

    handleDragMove(e) {
      if (!this.activePiece) return;
      e.preventDefault();

      const touch = e.touches ? e.touches[0] : e;
      const areaRect = this.piecesArea.getBoundingClientRect();

      let x = touch.clientX - areaRect.left - this.offsetX;
      let y = touch.clientY - areaRect.top - this.offsetY;

      x = Math.max(0, Math.min(x, areaRect.width - this.pieceSize));
      y = Math.max(0, Math.min(y, areaRect.height - this.pieceSize));

      this.activePiece.style.left = x + 'px';
      this.activePiece.style.top = y + 'px';
    }

    handleDragEnd() {
      if (!this.activePiece) return;

      const piece = this.activePiece;
      const pieceRow = parseInt(piece.dataset.row);
      const pieceCol = parseInt(piece.dataset.col);

      const pieceRect = piece.getBoundingClientRect();
      const pieceCenterX = pieceRect.left + pieceRect.width / 2;
      const pieceCenterY = pieceRect.top + pieceRect.height / 2;

      let snapped = false;

      for (const slot of this.slots) {
        if (slot.row !== pieceRow || slot.col !== pieceCol) continue;
        if (this.board[slot.row][slot.col]) continue;

        const slotRect = slot.element.getBoundingClientRect();
        const inX = pieceCenterX > slotRect.left && pieceCenterX < slotRect.right;
        const inY = pieceCenterY > slotRect.top && pieceCenterY < slotRect.bottom;

        if (inX && inY) {
          this.snapToSlot(piece, slot);
          snapped = true;
          break;
        }
      }

      piece.style.cursor = snapped ? 'default' : 'grab';
      piece.style.zIndex = snapped ? '50' : '100';
      piece.style.transform = 'scale(1)';
      piece.style.boxShadow = snapped ? 'none' : '0 3px 10px rgba(0,0,0,0.5)';

      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';

      this.activePiece = null;
    }

    snapToSlot(pieceEl, slot) {
      pieceEl.dataset.placed = 'true';
      this.board[slot.row][slot.col] = pieceEl;

      const slotRect = slot.element.getBoundingClientRect();
      const areaRect = this.piecesArea.getBoundingClientRect();

      pieceEl.style.left = (slotRect.left - areaRect.left + 1) + 'px';
      pieceEl.style.top = (slotRect.top - areaRect.top + 1) + 'px';
      pieceEl.style.width = (this.pieceSize - 2) + 'px';
      pieceEl.style.height = (this.pieceSize - 2) + 'px';
      pieceEl.style.pointerEvents = 'none';
      pieceEl.style.boxShadow = 'none';
      pieceEl.style.borderRadius = '0';

      const pieceData = this.pieces.find(p => p.element === pieceEl);
      if (pieceData) pieceData.placed = true;

      // Soft snap animation
      pieceEl.style.transform = 'scale(1.02)';
      setTimeout(() => pieceEl.style.transform = 'scale(1)', 120);

      this.checkWin();
    }

    checkWin() {
      const allPlaced = this.pieces.every(p => p.placed);
      if (allPlaced && !this.completed) {
        this.completed = true;
        
        // Subtle success glow on board
        this.boardArea.style.borderColor = 'rgba(74, 222, 128, 0.6)';
        this.boardArea.style.boxShadow = '0 0 20px rgba(74, 222, 128, 0.3)';
        
        // Show success overlay after brief pause
        setTimeout(() => this.showSuccess(), 600);
      }
    }

    showSuccess() {
      if (!this.successOverlay) return;

      this.successOverlay.hidden = false;
      // Trigger reflow for animation
      this.successOverlay.offsetHeight;
      this.successOverlay.classList.add('visible');
      this.successOverlay.style.opacity = '1';
    }

    playVideo() {
      if (!this.videoUrl || !this.videoContainer) return;
      
      const video = document.createElement('video');
      video.src = this.videoUrl;
      video.autoplay = true;
      video.muted = false; // Sound ON by default
      video.playsInline = true;
      video.controls = false;
      video.loop = true;
      video.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      
      // Tap to mute/unmute
      video.addEventListener('click', () => {
        video.muted = !video.muted;
      });
      
      const closeBtn = document.createElement('button');
      closeBtn.className = 'video-close-btn';
      closeBtn.innerHTML = '✕';
      closeBtn.onclick = () => this.closeVideo();
      
      this.videoContainer.innerHTML = '';
      this.videoContainer.appendChild(video);
      this.videoContainer.appendChild(closeBtn);
      this.videoContainer.hidden = false;
      this.videoContainer.style.opacity = '1';
      
      // Try to play with sound, fallback to muted if browser blocks it
      video.play().catch(() => {
        // Browser blocked autoplay with sound, start muted
        video.muted = true;
        video.play();
      });
    }

    closeVideo() {
      if (!this.videoContainer) return;
      
      const video = this.videoContainer.querySelector('video');
      if (video) {
        video.pause();
        video.src = '';
      }
      
      this.videoContainer.style.opacity = '0';
      setTimeout(() => {
        this.videoContainer.hidden = true;
        this.videoContainer.innerHTML = '';
      }, 300);
    }

    setupButtons() {
      // Flashlight / Hint button
      const flashlightBtn = this.section.querySelector('.flashlight');
      if (flashlightBtn) {
        flashlightBtn.onclick = () => this.showHint();
      }

      // Video button
      const videoBtn = document.querySelector(`#video-button-${this.sectionId}`);
      if (videoBtn) {
        videoBtn.onclick = () => this.playVideo();
      }

      // SMS form - PostScript integration
      const form = document.querySelector(`#sms-form-${this.sectionId}`);
      if (form) {
        form.onsubmit = async (e) => {
          e.preventDefault();
          const input = form.querySelector('input[type="tel"]');
          const phone = input?.value.trim();
          if (!phone) return;

          const btn = form.querySelector('button[type="submit"]');
          const originalText = btn?.textContent;
          if (btn) {
            btn.disabled = true;
            btn.textContent = '...';
          }

          try {
            const shopId = form.dataset.psShopId || '778092';
            const keywordId = form.dataset.psKeywordId || '693622';

            const formData = new FormData();
            formData.append('shop_id', shopId);
            formData.append('keyword_id', keywordId);
            formData.append('phone', phone);

            await fetch('https://api.postscript.io/subscribe/form', {
              method: 'POST',
              body: formData,
              mode: 'no-cors'
            });
            
            this.showRewardCode();
          } catch(e) {
            if (btn) {
              btn.disabled = false;
              btn.textContent = originalText;
            }
          }
        };
      }
    }

    getDiscountCode() {
      // Check if user already has a code
      const stored = localStorage.getItem('lafortune_code');
      if (stored) return stored;
      
      // Get random code from pool
      if (this.codePool && this.codePool.length > 0) {
        const code = this.codePool[Math.floor(Math.random() * this.codePool.length)];
        localStorage.setItem('lafortune_code', code);
        return code;
      }
      return 'LAFORTUNE10';
    }

    showRewardCode() {
      const code = this.getDiscountCode();
      const successContent = this.successOverlay?.querySelector('.puzzle-success-content');
      
      if (!successContent) return;
      
      // Fade out current content and replace with reward
      successContent.style.opacity = '0';
      successContent.style.transform = 'scale(0.95)';
      
      setTimeout(() => {
        successContent.innerHTML = `
          <div class="reward-card">
            <div class="success-glow"></div>
            <p class="reward-unlocked">Unlocked</p>
            <div class="reward-code-container">
              <span class="reward-code" id="reward-code-${this.sectionId}">${code}</span>
              <button class="reward-copy-btn" id="copy-code-${this.sectionId}" aria-label="Copy code">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
            </div>
            <p class="reward-hint">Use at checkout</p>
          </div>
        `;
        
        successContent.style.opacity = '1';
        successContent.style.transform = 'scale(1)';
        
        // Copy button functionality
        const copyBtn = document.getElementById(`copy-code-${this.sectionId}`);
        if (copyBtn) {
          copyBtn.onclick = () => {
            navigator.clipboard.writeText(code).then(() => {
              copyBtn.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              `;
              setTimeout(() => {
                copyBtn.innerHTML = `
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                `;
              }, 2000);
            });
          };
        }
      }, 300);
    }

    showHint() {
      // Create hint popup
      const popup = document.createElement('div');
      popup.className = 'hint-popup';
      popup.innerHTML = `
        <div class="hint-popup-content">
          <div class="hint-glow"></div>
          ${this.hintImageUrl ? `<img src="${this.hintImageUrl}" alt="Hint" class="hint-image">` : ''}
          <p class="hint-message">You've found the hint 😉</p>
          <div class="hint-timer"></div>
        </div>
      `;
      
      // Add to screen
      const screen = this.section.querySelector('.iphone-screen');
      if (screen) {
        screen.appendChild(popup);
        
        // Trigger animation
        requestAnimationFrame(() => {
          popup.classList.add('visible');
        });
        
        // Auto close after 3 seconds
        setTimeout(() => {
          popup.classList.remove('visible');
          setTimeout(() => popup.remove(), 300);
        }, 3000);
      }
    }
  }

  window.LafortunePuzzle = LafortunePuzzle;
})();
